import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Tenant } from '@hyvay/shared-types';
import { useAuth } from '../../contexts/AuthContext';
import { tenantService } from '../../services/tenant.service';
import { templateService, type Template } from '../../services/template.service';
import { mediaService, type MediaAsset } from '../../services/studio.service';
import { accountService } from '../../services/account.service';
import { useToast } from '../../components/common/Toast';
import { SEOHead } from '../../components/seo/SEOHead';
import { generateOrganizationSchema, generateWebSiteSchema } from '../../utils/structured-data';
import { LandingNav } from './LandingNav';
import { BootOverlay, startBoot, clearBoot, type BootCopy } from './BootOverlay';
import { HeroSection, type Platform } from './HeroSection';
import { HowItWorks } from './HowItWorks';
import { WorkspaceSection, type WorkMedia, type WorkProject } from './WorkspaceSection';
import { TemplatesSection } from './TemplatesSection';
import { toTplCard } from './TemplateCard';
import { QuoteSection } from './QuoteSection';
import { CtaSection } from './CtaSection';
import { TrustedBySection } from './TrustedBySection';
import { LandingFooter } from './LandingFooter';
import { TopUpDialog, type SettleStatus } from './TopUpDialog';
import { useLandingMotion } from './useLandingMotion';
import { useLandingTheme } from './useLandingTheme';
import { CHIPS, FONT_MONO, FONT_SANS, HERO_ANNOUNCEMENT, PLACEHOLDER_EXAMPLES, type Chip } from './landingData';
import { fmtBytes, fmtUpdated } from './format';
import { trackAppCreation } from '../../components/analytics/GoogleAnalytics';
import { useApplyTemplate } from './useApplyTemplate';
import './landing.css';

const toProject = (t: Tenant): WorkProject => {
    const s = String(t.status || 'DRAFT').toUpperCase();
    return {
        id: t.id,
        name: t.name,
        meta: t.description || (s === 'PUBLISHED' ? 'Published site' : 'Draft'),
        updated: fmtUpdated(t.updatedAt),
        status: s === 'PUBLISHED' ? 'LIVE' : s === 'BUILDING' ? 'BUILDING' : 'DRAFT',
        img: t.thumbnailUrl || null,
    };
};

const toMedia = (a: MediaAsset): WorkMedia => ({
    id: a.assetId,
    name: a.originalName,
    kind: a.kind === 'video' ? 'Video' : a.mimeType === 'image/svg+xml' ? 'Vector' : 'Image',
    size: fmtBytes(a.bytes),
    updated: fmtUpdated(a.createdAt, 'Uploaded'),
    img: a.kind === 'image' ? a.url : null,
    url: a.url,
});

export const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated, isLoading: authLoading, logout, refreshUser } = useAuth();
    const rootRef = useRef<HTMLDivElement>(null);

    const [theme, toggleTheme] = useLandingTheme();
    const [prompt, setPrompt] = useState('');
    const [platform, setPlatform] = useState<Platform>('web');
    const [chipOffset, setChipOffset] = useState(0);
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [media, setMedia] = useState<MediaAsset[]>([]);
    const [wsLoading, setWsLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    const [templates, setTemplates] = useState<Template[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(true);
    // Set by the hero's "NEW" pill; TemplatesSection pages to the template and rings it.
    const [tplFocus, setTplFocus] = useState<{ id: string; nonce: number } | null>(null);

    const [credits, setCredits] = useState<number | null>(null);
    const [topUpOpen, setTopUpOpen] = useState(false);
    const toast = useToast();
    const showStatus = (status: SettleStatus, message: string) =>
        toast.show(message, { tone: status === 'SUCCESS' ? 'success' : status === 'PENDING' ? 'warn' : 'error', label: status, duration: 6000 });

    useLandingMotion(rootRef, [isAuthenticated, wsLoading, templatesLoading]);

    // ---- rotating placeholder ----
    useEffect(() => {
        const id = setInterval(() => setPlaceholderIndex((i) => (i + 1) % PLACEHOLDER_EXAMPLES.length), 3400);
        return () => clearInterval(id);
    }, []);

    // ---- workspace data: only once auth has resolved AND the visitor is logged in.
    // An anonymous request would 401 and api.ts would bounce them to /login. ----
    const loadWorkspace = useCallback(async () => {
        setWsLoading(true);
        const [t, m] = await Promise.all([tenantService.getAllTenants(), mediaService.list()]);
        if (t.success) setTenants(t.data);
        if (m.success && m.data) setMedia(m.data.assets);
        setWsLoading(false);
    }, []);

    useEffect(() => {
        if (authLoading) return;
        if (isAuthenticated) {
            void loadWorkspace();
            void accountService.getCredits().then((r) => { if (r.success && r.data) setCredits(r.data.credits); });
        } else {
            setTenants([]); setMedia([]); setCredits(null); setWsLoading(false);
        }
    }, [authLoading, isAuthenticated, loadWorkspace]);

    useEffect(() => {
        if (isAuthenticated && credits === null && typeof user?.credits === 'number') setCredits(user.credits);
    }, [isAuthenticated, user, credits]);

    // While any app is building, poll so its pill flips when generation finishes.
    useEffect(() => {
        if (!tenants.some((t) => String(t.status).toUpperCase() === 'BUILDING')) return;
        const id = setInterval(async () => {
            const r = await tenantService.getAllTenants();
            if (r.success) setTenants(r.data);
        }, 10_000);
        return () => clearInterval(id);
    }, [tenants]);

    // ---- templates (public catalog) ----
    useEffect(() => {
        void (async () => {
            const res = await templateService.list();
            if (res.success && res.data) setTemplates(res.data);
            setTemplatesLoading(false);
        })();
    }, []);

    // ---- composer ----
    const focusComposer = useCallback(() => {
        const el = document.querySelector<HTMLElement>('.hv-composer');
        if (!el) return;
        const target = el.getBoundingClientRect().top - 120;
        window.scrollTo({ top: window.scrollY + target, behavior: 'smooth' });
        const ta = el.querySelector('textarea');
        if (ta) setTimeout(() => ta.focus({ preventScroll: true }), 620);
    }, []);

    // Signup hands over the result of its "Referral code" box: show it once.
    useEffect(() => {
        const notice = (location.state as { codeNotice?: { tone: 'success' | 'error' | 'warn'; text: string } } | null)?.codeNotice;
        if (!notice) return;
        toast.show(notice.text, { tone: notice.tone, label: notice.tone === 'success' ? 'KODE' : notice.tone === 'warn' ? 'INFO' : 'KODE', duration: 8000 });
        navigate('/', { replace: true, state: null });
    }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

    // A use-case page's "Build one like this" lands here with a seed prompt in
    // router state: fill the composer, clear the state so a reload doesn't repeat it.
    useEffect(() => {
        const seed = (location.state as { prompt?: string } | null)?.prompt;
        if (!seed) return;
        setPrompt(seed);
        navigate('/', { replace: true, state: null });
        const id = setTimeout(focusComposer, 80);
        return () => clearTimeout(id);
    }, [location.state, navigate, focusComposer]);

    // Arriving at "/#templates" from /pricing or a use-case page: the section only
    // exists after this page renders, so the browser's native hash scroll missed it.
    useEffect(() => {
        if (!location.hash) return;
        const el = document.getElementById(location.hash.slice(1));
        if (!el) return;
        const id = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
        return () => clearTimeout(id);
    }, [location.hash]);

    const chips: Chip[] = useMemo(() => {
        const o = chipOffset % CHIPS.length;
        return CHIPS.slice(o).concat(CHIPS.slice(0, o)).slice(0, 5);
    }, [chipOffset]);

    // The design's launch overlay. It goes up on the click, the real work (create
    // the project, apply the template) runs underneath, and the studio keeps the
    // same overlay until 2.2 s have passed and its data is loaded.
    const [boot, setBoot] = useState<BootCopy | null>(null);
    const launch = (copy: BootCopy) => { setBoot(copy); startBoot(copy); };
    const abortLaunch = () => { setBoot(null); clearBoot(); };

    const handleSubmit = async () => {
        if (!prompt.trim() || submitting) return;
        if (!isAuthenticated) { navigate('/login'); return; }
        setSubmitting(true);
        launch('BUILDING YOUR SITE');
        // No name is sent: the server derives one from the prompt (AppNamerService).
        const response = await tenantService.createTenant({ description: prompt });
        if (response.success && response.data) {
            const appId = response.data.id;
            trackAppCreation('prompt');
            // Hand the prompt to the studio via sessionStorage and do a FULL page load:
            // only /studio routes are served with the COOP/COEP headers the WebContainer needs.
            sessionStorage.setItem(`hyvay:initial-prompt:${appId}`, prompt);
            window.location.assign(`/studio/${appId}`);
            return;
        }
        abortLaunch();
        showStatus('FAILED', (response as any).message || 'Could not create the project. Please try again.');
        setSubmitting(false);
    };

    const openProject = (id: string) => {
        launch('OPENING YOUR PROJECT');
        window.location.assign(`/studio/${id}`);
    };

    const { applying: applyingTemplate, apply: applyTemplate } = useApplyTemplate({
        isAuthenticated,
        launch,
        abortLaunch,
        onCreatedEmpty: (t) => setTenants((prev) => [...prev, t]),
    });
    const handleUseTemplate = (templateId: string) => {
        const template = templates.find((t) => t.id === templateId);
        if (template) void applyTemplate(template);
    };

    const handleUpload = async (file: File) => {
        setUploading(true);
        const res = await mediaService.upload(file);
        if (res.success && res.data) setMedia((prev) => [res.data, ...prev]);
        else showStatus('FAILED', (res as any).message || 'Upload gagal.');
        setUploading(false);
    };

    const onTopUpSettled = async (status: SettleStatus, message: string) => {
        showStatus(status, message);
        if (status === 'SUCCESS') {
            await refreshUser();
            const r = await accountService.getCredits();
            if (r.success && r.data) setCredits(r.data.credits);
        }
    };

    const projects = useMemo(() => tenants.map(toProject), [tenants]);
    const mediaRows = useMemo(() => media.map(toMedia), [media]);
    const tplCards = useMemo(() => templates.map(toTplCard), [templates]);

    return (
        <div ref={rootRef} className="hv-root" data-hv-theme={theme} style={{ position: 'static', fontFamily: FONT_SANS, color: 'var(--hv-ink)', background: 'var(--hv-desk)', overflowX: 'clip', minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>
            <SEOHead
                title="HyVay - Platform No-Code App Builder Indonesia"
                description="Buat aplikasi web profesional tanpa coding dengan HyVay. Platform No-Code App Builder terbaik di Indonesia. Cepat, Mudah, dan Terjangkau."
                keywords={['no-code', 'app builder', 'indonesia', 'bikin aplikasi', 'website builder', 'startup']}
                structuredData={[generateOrganizationSchema(), generateWebSiteSchema()]}
            />

            <HeroSection
                nav={(
                    <LandingNav
                        theme={theme}
                        onToggleTheme={toggleTheme}
                        isAuthenticated={isAuthenticated}
                        avatarUrl={user?.avatar}
                        userName={user?.fullName}
                        onLogout={logout}
                    />
                )}
                prompt={prompt}
                onPrompt={setPrompt}
                onSubmit={handleSubmit}
                submitting={submitting}
                platform={platform}
                onPlatform={setPlatform}
                credits={isAuthenticated ? (credits ?? 0) : null}
                onOpenTopUp={() => setTopUpOpen(true)}
                chips={chips}
                onShuffle={() => setChipOffset((o) => o + 3)}
                onFillChip={(c) => setPrompt(c.prompt)}
                placeholderIndex={placeholderIndex}
                placeholderText={PLACEHOLDER_EXAMPLES[placeholderIndex % PLACEHOLDER_EXAMPLES.length]}
                announcement={HERO_ANNOUNCEMENT.label}
                onAnnouncement={() => setTplFocus({ id: HERO_ANNOUNCEMENT.templateId, nonce: Date.now() })}
            />

            <HowItWorks />

            <WorkspaceSection
                isAuthenticated={isAuthenticated}
                loading={authLoading || (isAuthenticated && wsLoading)}
                projects={projects}
                media={mediaRows}
                uploading={uploading}
                onOpenProject={openProject}
                onCreateProject={focusComposer}
                onUploadFile={handleUpload}
                onSignIn={() => navigate('/login')}
            />

            <TemplatesSection templates={tplCards} loading={templatesLoading} busyId={applyingTemplate} onUse={handleUseTemplate} focus={tplFocus} />

            <QuoteSection />

            <TrustedBySection />

            <CtaSection onStart={focusComposer} />

            <LandingFooter />

            <TopUpDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} onSettled={onTopUpSettled} />

            {boot && <BootOverlay copy={boot} dark={theme === 'dark'} />}

        </div>
    );
};

export default LandingPage;
